import type { SystemView } from "../types";

export type TicketPriority = "critical" | "high" | "medium" | "low";
export type TicketStatus = "open" | "in-progress" | "waiting" | "resolved" | "closed";

export interface TicketComment {
    id: string;
    author: string;
    text: string;
    time: string;
}

export interface TicketStatusChange {
    from: TicketStatus;
    to: TicketStatus;
    by: string;
    time: string;
}

export interface Ticket {
    id: string;
    title: string;
    description: string;
    priority: TicketPriority;
    status: TicketStatus;
    assignee: string;
    reporter: string;
    system: SystemView;
    relatedEventIds: string[];
    createdAt: string;
    updatedAt: string;
    comments: TicketComment[];
    statusHistory: TicketStatusChange[];
}

export const MOCK_TICKETS: Ticket[] = [
    {
        id: "TKT-001",
        title: "Investigate brute force attack from 185.143.223.1",
        description:
            "Persistent brute force attempts detected against fw-prod-01. NDR has blocked lateral movement but the source IP continues scanning. Need to assess full blast radius and coordinate with ISP for takedown.",
        priority: "critical",
        status: "in-progress",
        assignee: "M. Schmidt",
        reporter: "SOC Auto",
        system: "soc",
        relatedEventIds: ["SOC-1247", "NDR-421"],
        createdAt: "2026-03-13 14:25",
        updatedAt: "2026-03-13 15:10",
        comments: [
            {
                id: "c1",
                author: "M. Schmidt",
                text: "Confirmed source IP belongs to known APT-Sigma-7 infrastructure. Escalating to incident response.",
                time: "14:45",
            },
            {
                id: "c2",
                author: "L. Weber",
                text: "NDR block rules updated. All traffic from 185.143.223.0/24 is now dropped at perimeter.",
                time: "15:02",
            },
            {
                id: "c3",
                author: "M. Schmidt",
                text: "ISP abuse report submitted. Awaiting confirmation.",
                time: "15:10",
            },
        ],
        statusHistory: [{ from: "open", to: "in-progress", by: "M. Schmidt", time: "14:30" }],
    },
    {
        id: "TKT-002",
        title: "Malware on edr-ws-142 — quarantine and remediate",
        description:
            "Malware detected on workstation edr-ws-142, traced to mail quarantine Q-4821. C2 beacon was active before NDR blocked it. Full endpoint wipe and rebuild may be required.",
        priority: "high",
        status: "in-progress",
        assignee: "K. Fischer",
        reporter: "SOC Auto",
        system: "soc",
        relatedEventIds: ["SOC-1248", "MAIL-4824"],
        createdAt: "2026-03-13 14:20",
        updatedAt: "2026-03-13 14:55",
        comments: [
            {
                id: "c4",
                author: "K. Fischer",
                text: "Endpoint isolated from network. Beginning forensic analysis.",
                time: "14:35",
            },
            {
                id: "c5",
                author: "K. Fischer",
                text: "C2 beacon confirmed inactive after NDR block. Collecting memory dump.",
                time: "14:55",
            },
        ],
        statusHistory: [{ from: "open", to: "in-progress", by: "K. Fischer", time: "14:30" }],
    },
    {
        id: "TKT-003",
        title: "Patch OpenSSL CVE on lb-prod-02",
        description:
            "PT-004 identified critical OpenSSL vulnerability on load balancer lb-prod-02. Same CVE is being actively exploited via mail-delivered malware payloads. Patch window needs to be scheduled.",
        priority: "high",
        status: "waiting",
        assignee: "T. Braun",
        reporter: "Pentest Team",
        system: "pentests",
        relatedEventIds: ["PT-004", "MAIL-4824"],
        createdAt: "2026-03-12 09:00",
        updatedAt: "2026-03-13 11:30",
        comments: [
            {
                id: "c6",
                author: "T. Braun",
                text: "Patch tested in staging. Requesting maintenance window for Saturday 03:00.",
                time: "2026-03-12 16:00",
            },
            {
                id: "c7",
                author: "J. Hoffmann",
                text: "Maintenance window approved for Saturday 2026-03-15 03:00-05:00 UTC.",
                time: "2026-03-13 11:30",
            },
        ],
        statusHistory: [
            { from: "open", to: "in-progress", by: "T. Braun", time: "2026-03-12 10:00" },
            { from: "in-progress", to: "waiting", by: "T. Braun", time: "2026-03-12 16:00" },
        ],
    },
    {
        id: "TKT-004",
        title: "SQL injection remediation for api.example.com",
        description:
            "PT-001 found critical SQL injection in the public API. Live exploitation attempt was detected by SOC. Immediate fix required before next scan cycle.",
        priority: "critical",
        status: "in-progress",
        assignee: "A. Richter",
        reporter: "Pentest Team",
        system: "pentests",
        relatedEventIds: ["PT-001", "SOC-1247"],
        createdAt: "2026-03-11 10:00",
        updatedAt: "2026-03-13 13:00",
        comments: [
            {
                id: "c8",
                author: "A. Richter",
                text: "Parameterized queries implemented. PR submitted for review.",
                time: "2026-03-12 15:00",
            },
            {
                id: "c9",
                author: "M. Schmidt",
                text: "WAF rule added as temporary mitigation until deploy.",
                time: "2026-03-13 09:00",
            },
            {
                id: "c10",
                author: "A. Richter",
                text: "Deploy scheduled for today 16:00. Requesting SOC to monitor.",
                time: "2026-03-13 13:00",
            },
        ],
        statusHistory: [{ from: "open", to: "in-progress", by: "A. Richter", time: "2026-03-11 11:00" }],
    },
    {
        id: "TKT-005",
        title: "Phishing campaign follow-up: 29 clickers need training",
        description:
            "Q2 IT Helpdesk phishing simulation flagged 29 users who clicked. SOC ran endpoint scans, ZTNA scores were lowered. Users need mandatory security awareness re-training.",
        priority: "medium",
        status: "open",
        assignee: "S. Koch",
        reporter: "Phishing Team",
        system: "phishing",
        relatedEventIds: ["PHISH-198", "ZTNA-089"],
        createdAt: "2026-03-13 14:00",
        updatedAt: "2026-03-13 14:00",
        comments: [],
        statusHistory: [],
    },
    {
        id: "TKT-006",
        title: "ZTNA session anomaly — s.braun investigation",
        description:
            "User s.braun attempted VPN from Bucharest with unknown device. Trust score dropped to 23. NDR flagged associated C2 traffic. Account may be compromised.",
        priority: "critical",
        status: "in-progress",
        assignee: "M. Schmidt",
        reporter: "ZTNA Auto",
        system: "ztna",
        relatedEventIds: ["ZTNA-091", "NDR-421", "SOC-1247"],
        createdAt: "2026-03-13 14:22",
        updatedAt: "2026-03-13 15:05",
        comments: [
            {
                id: "c11",
                author: "M. Schmidt",
                text: "Contacted s.braun — confirms they are not in Bucharest. Account credentials likely compromised.",
                time: "14:40",
            },
            {
                id: "c12",
                author: "M. Schmidt",
                text: "Password reset forced. All active sessions revoked. MFA re-enrollment required.",
                time: "15:05",
            },
        ],
        statusHistory: [{ from: "open", to: "in-progress", by: "M. Schmidt", time: "14:25" }],
    },
    {
        id: "TKT-007",
        title: "DNS tunneling investigation — 91.215.85.12",
        description:
            "NDR detected DNS tunneling to external IP 91.215.85.12 linked to malware from Mail Gateway quarantine. Need to determine scope of data exfiltration.",
        priority: "high",
        status: "in-progress",
        assignee: "L. Weber",
        reporter: "NDR Auto",
        system: "ndr",
        relatedEventIds: ["NDR-415", "SOC-1249"],
        createdAt: "2026-03-13 14:05",
        updatedAt: "2026-03-13 14:50",
        comments: [
            {
                id: "c13",
                author: "L. Weber",
                text: "DNS query logs analyzed. ~2.3MB of encoded data transmitted over 47 minutes before block.",
                time: "14:30",
            },
            {
                id: "c14",
                author: "L. Weber",
                text: "Content appears to be directory listings from file server. No PII detected in sample.",
                time: "14:50",
            },
        ],
        statusHistory: [{ from: "open", to: "in-progress", by: "L. Weber", time: "14:10" }],
    },
    {
        id: "TKT-008",
        title: "MFA bypass finding — enforce hardware tokens",
        description:
            "PT-003 demonstrated MFA bypass on AD DC-01 for privileged accounts. ZTNA policy was updated to require hardware tokens. Need to verify rollout completion.",
        priority: "medium",
        status: "resolved",
        assignee: "J. Hoffmann",
        reporter: "Pentest Team",
        system: "pentests",
        relatedEventIds: ["PT-003", "ZTNA-082"],
        createdAt: "2026-03-10 09:00",
        updatedAt: "2026-03-13 10:00",
        comments: [
            {
                id: "c15",
                author: "J. Hoffmann",
                text: "Hardware tokens distributed to all 23 privileged accounts.",
                time: "2026-03-11 14:00",
            },
            {
                id: "c16",
                author: "J. Hoffmann",
                text: "All privileged accounts confirmed enrolled with hardware MFA. Marking as resolved.",
                time: "2026-03-13 10:00",
            },
        ],
        statusHistory: [
            { from: "open", to: "in-progress", by: "J. Hoffmann", time: "2026-03-10 10:00" },
            { from: "in-progress", to: "resolved", by: "J. Hoffmann", time: "2026-03-13 10:00" },
        ],
    },
    {
        id: "TKT-009",
        title: "Default printer credentials — schedule rotation",
        description:
            "PT-006 found default credentials on printer-3f-east. ZTNA segmentation is in place but credentials should still be rotated as a matter of policy.",
        priority: "low",
        status: "open",
        assignee: "T. Braun",
        reporter: "Pentest Team",
        system: "pentests",
        relatedEventIds: ["PT-006"],
        createdAt: "2026-03-12 14:00",
        updatedAt: "2026-03-12 14:00",
        comments: [],
        statusHistory: [],
    },
];
